import React, { useEffect, useState, useCallback, useMemo } from "react";
import Context from "./index";
import {
  fetchAllNotifications,
  fetchNotificationById,
} from "../../Api/ApiMethods";
import { apiClient } from "../../Api/client";
import { toast } from "react-toastify";

const NotificationApiUrl =
  process.env.REACT_APP_LACIVERT_API_ENDPOINT + "/notification";

const NotificationProvider = (props) => {
  const [allNotifications, setAllNotifications] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const getNotifications = useCallback(async (params = { count: 10 }) => {
    setLoading(true);
    const result = await fetchAllNotifications({
      apiEndpoint: NotificationApiUrl,
      params: params,
    });

    if (result) {
      setAllNotifications(result.items);
      setTotalRows(result.total);
      setUnreadCount(result.items.filter((n) => !n.isRead).length);
    } else {
      setAllNotifications([]);
      setTotalRows(0);
      setUnreadCount(0);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const isAuthenticated = !!localStorage.getItem("accessToken");
    if (isAuthenticated) {
      getNotifications();
    }

    const handleSessionChange = () => {
      if (localStorage.getItem("accessToken")) {
        getNotifications();
      }
    };
    window.addEventListener("sessionChanged", handleSessionChange);
    return () =>
      window.removeEventListener("sessionChanged", handleSessionChange);
  }, [getNotifications]);

  const RemoveNotification = useCallback(
    async (id) => {
      setLoading(true);
      try {
        await apiClient.delete(`/notification/${id}`);
        toast.success("Bildirim başarıyla silindi!");
        setAllNotifications((prev) => prev.filter((n) => n.id !== id));
        setUnreadCount((prev) =>
          allNotifications.find((n) => n.id === id && !n.isRead)
            ? prev - 1
            : prev,
        );
      } catch (error) {
        console.error(`Error deleting notification with id ${id}:`, error);
        toast.error(
          `Bildirim silinirken hata oluştu: ${error.response?.data?.message || error.message}`,
        );
      } finally {
        setLoading(false);
      }
    },
    [allNotifications, getNotifications],
  );

  const MarkNotificationAsRead = useCallback(
    async (id) => {
      const notification = allNotifications.find((n) => n.id === id);
      if (!notification || notification.isRead) return;

      setLoading(true);
      try {
        toast.info("Bildirim okundu olarak işaretlendi.");
        setAllNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)),
        );
        setUnreadCount((prev) => prev - 1);
      } catch (error) {
        console.error(`Error marking notification ${id} as read:`, error);
        toast.error(
          `Bildirim işaretlenirken hata oluştu: ${error.response?.data?.message || error.message}`,
        );
      } finally {
        setLoading(false);
      }
    },
    [allNotifications],
  );

  const GetNotificationById = useCallback(async (id) => {
    try {
      const notification = await fetchNotificationById(NotificationApiUrl, id);
      if (notification) {
        // Don't call MarkNotificationAsRead here to avoid re-rendering the list
        // The notification will be marked as read on the server side when fetched
        return notification;
      }
      return null;
    } catch (error) {
      console.error(`Error fetching notification ${id}:`, error);
      toast.error(
        `Bildirim detayları getirilirken hata oluştu: ${error.response?.data?.message || error.message}`,
      );
      return null;
    }
  }, []); // Remove MarkNotificationAsRead dependency

  const contextValue = useMemo(
    () => ({
      allNotifications,
      totalRows,
      loading,
      unreadCount,
      getNotifications,
      RemoveNotification,
      MarkNotificationAsRead,
      GetNotificationById,
    }),
    [
      allNotifications,
      totalRows,
      loading,
      unreadCount,
      getNotifications,
      RemoveNotification,
      MarkNotificationAsRead,
      GetNotificationById,
    ],
  );

  return (
    <Context.Provider value={contextValue}>{props.children}</Context.Provider>
  );
};

export default NotificationProvider;
